import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { jwtDecode } from 'jwt-decode'
import { api } from '@/services/api'
import { identifyUser } from '@/services/analytics'
import type { AuthUser } from '@/services/authClient'

interface DecodedToken {
  sub: string
  name?: string
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
  dateOfBirth?: string
  gender?: string
  church?: string
  city?: string
  postalCode?: string
  address?: string
  profession?: string
  role?: string | null
  stripeCustomerId?: string | null
  exp: number
}

export const useUserStore = defineStore('user', () => {
  // State
  const isLogged = ref(false)
  const token = ref<string | null>(null)
  const id = ref<string | null>(null)
  const firstName = ref<string | null>(null)
  const lastName = ref<string | null>(null)
  const email = ref<string | null>(null)
  const mobile = ref<string | null>(null)
  const expiresOn = ref<Date | null>(null)
  const dateOfBirth = ref<Date | null>(null)
  const gender = ref<string | null>(null)
  const church = ref<string | null>(null)
  const city = ref<string | null>(null)
  const postalCode = ref<string | null>(null)
  const address = ref<string | null>(null)
  const profession = ref<string | null>(null)
  const stripeCustomerId = ref<string | null>(null)
  const isAdmin = ref(false)

  // Getters
  const name = computed(() => {
    if (firstName.value && lastName.value) {
      return `${firstName.value} ${lastName.value}`
    }
    return null
  })

  const isTokenExpired = computed(() => {
    if (!expiresOn.value) return true
    return new Date() > expiresOn.value
  })

  // Actions
  function setLogin(newToken: string, authUser?: AuthUser) {
    const decoded = jwtDecode<DecodedToken>(newToken)
    const roleValue = decoded.role ?? authUser?.role ?? null

    id.value = decoded.sub
    email.value = decoded.email ?? authUser?.email ?? null
    firstName.value = decoded.firstName ?? authUser?.firstName ?? null
    lastName.value = decoded.lastName ?? authUser?.lastName ?? null
    token.value = newToken
    isLogged.value = true
    isAdmin.value = (roleValue ?? '').toLowerCase() === 'admin'
    expiresOn.value = new Date(decoded.exp * 1000)
    dateOfBirth.value = decoded.dateOfBirth ? new Date(decoded.dateOfBirth) : null
    gender.value = decoded.gender ?? authUser?.gender ?? null
    mobile.value = decoded.phone ?? authUser?.phone ?? null
    church.value = decoded.church ?? authUser?.church ?? null
    city.value = decoded.city ?? authUser?.city ?? null
    postalCode.value = decoded.postalCode ?? authUser?.postalCode ?? null
    address.value = decoded.address ?? authUser?.address ?? null
    profession.value = decoded.profession ?? authUser?.profession ?? null
    stripeCustomerId.value = decoded.stripeCustomerId ?? null

    api.setToken(`Bearer ${newToken}`)

    // Save user state to localStorage for instant display on page reload
    const initials = ((firstName.value ?? '')?.charAt(0) || '') + ((lastName.value ?? '')?.charAt(0) || '')
    if (initials) {
      localStorage.setItem('userInitials', initials)
    }
    // hasEventsNav and hasAdminNav are set by updateNavStorageFlags() in App.vue
    // after eventUserRoles are loaded (handles both global admin and event-specific admin)

    // Exclude admins from analytics — their activity would otherwise skew
    // real-user numbers. Umami reads this flag at script init, so the
    // change takes effect on the next page load. For non-admins we clear
    // the flag in case it was set previously (role could have been revoked).
    if (isAdmin.value) {
      localStorage.setItem('umami.disabled', '1')
    } else {
      localStorage.removeItem('umami.disabled')
      // Only tag non-admin sessions in Umami so analytics stay meaningful.
      if (import.meta.env.PROD && email.value) {
        identifyUser(email.value, {
          id: id.value,
          name: name.value || '',
          email: email.value || '',
        })
      }
    }
  }

  function setLogout() {
    id.value = null
    firstName.value = null
    lastName.value = null
    email.value = null
    token.value = null
    isLogged.value = false
    isAdmin.value = false
    expiresOn.value = null
    dateOfBirth.value = null
    gender.value = null
    mobile.value = null
    church.value = null
    city.value = null
    postalCode.value = null
    address.value = null
    profession.value = null
    stripeCustomerId.value = null

    api.setToken(null)
    localStorage.removeItem('userInitials')
    localStorage.removeItem('hasEventsNav')
    localStorage.removeItem('hasAdminNav')
  }

  async function logout() {
    setLogout()
  }

  return {
    // State
    isLogged,
    token,
    id,
    firstName,
    lastName,
    email,
    mobile,
    expiresOn,
    dateOfBirth,
    gender,
    church,
    city,
    postalCode,
    address,
    profession,
    stripeCustomerId,
    isAdmin,
    // Getters
    name,
    isTokenExpired,
    // Actions
    setLogin,
    setLogout,
    logout,
  }
})
